import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import { 
  Trophy, CheckCircle, Award, MapPin, Play, LogOut, BookOpen, Clock, AlertTriangle, Download, Flame, TrendingUp, HelpCircle, ClipboardList, Sun, Moon, ShoppingBag, Coins
} from 'lucide-react';
import './StudentDashboard.css';
import Leaderboard from './Leaderboard';
import UserGuide from './UserGuide';
import Store from './Store';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const TYPE_LABELS = {
  desk: '🍽️ Сервірування',
  game: '🎮 Ігровий сценарій',
  quiz: '📝 Квіз',
  complex: '⚡ Комплексний тест',
  'multi-desk': '🥞 Комплексне сервірування',
  pizza: '🍕 Конструктор піци',
  course: '📖 Курс навчання'
};

const StudentDashboard = ({ onLogout, user, theme, toggleTheme }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('student_active_tab') || 'courses';
  });
  const [historyPage, setHistoryPage] = useState(1);
  const { error } = useToast();

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    localStorage.setItem('student_active_tab', tabName);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [dashRes, assignmentsRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/students/me/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/assignments/my`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/courses/student/my`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        const resourceTypeMap = {
          'Quiz': 'quiz',
          'DeskTemplate': 'desk',
          'MultiDeskTest': 'multi-desk',
          'GameScenario': 'game',
          'ComplexTest': 'complex',
          'PizzaTemplate': 'pizza'
        };

        const mappedTasks = assignmentsRes.data.map(ass => ({
          id: ass._id,
          hash: ass.hash,
          type: resourceTypeMap[ass.resourceType] || ass.resourceType,
          title: ass.title,
          description: ass.description,
          isCompleted: ass.isCompleted,
          score: ass.score,
          percentage: ass.percentage,
          completedAt: ass.completedAt,
          createdAt: ass.createdAt
        }));

        setData({
          ...dashRes.data,
          tasks: mappedTasks,
          courses: coursesRes.data
        });

        // Sync local storage user profile with latest student details
        const savedUserStr = localStorage.getItem('user');
        if (savedUserStr && dashRes.data?.student) {
          try {
            const savedUser = JSON.parse(savedUserStr);
            savedUser.studentName = dashRes.data.student.studentName || '';
            savedUser.studentLastName = dashRes.data.student.studentLastName || '';
            savedUser.studentCity = dashRes.data.student.studentCity || savedUser.city || '';
            savedUser.position = dashRes.data.student.position || '';
            localStorage.setItem('user', JSON.stringify(savedUser));
          } catch (e) {
            console.error('Error updating cached student user info:', e);
          }
        }
        
        const savedTab = localStorage.getItem('student_active_tab');
        if (!savedTab) {
          const hasUncompleted = mappedTasks.some(t => !t.isCompleted);
          if (hasUncompleted) {
            handleTabChange('tasks');
          }
        }
      } catch (err) {
        error(err.response?.data?.error || 'Помилка при завантаженні кабінету');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();

    // Real-time socket listener for auto-refreshing assignments without reloading
    const token = localStorage.getItem('token');
    const socket = io(API_URL.replace('/api', ''), {
      transports: ['websocket'],
      auth: { token }
    });

    socket.on('new_assignment', () => {
      fetchDashboardData();
    });

    socket.on('course_updated', () => {
      fetchDashboardData();
    });

    return () => {
      socket.disconnect();
    };
  }, [error]);

  if (loading) {
    return (
      <div className="sd-loading">
        <div className="spinner"></div>
        <p>Завантаження кабінету навчання...</p>
      </div>
    );
  }

  if (!data) return null;

  const { student, history, recommendations, tasks, certificates = [] } = data;

  const completedCoursesHistory = (data.courses || [])
    .filter(item => item.progress?.isCompleted)
    .map(item => ({
      type: 'course',
      title: item.course.title,
      completedAt: item.progress.completedAt || item.progress.updatedAt || new Date(),
      percentage: item.progress.percentage || 100,
      score: item.progress.completedSteps,
      total: item.progress.totalSteps,
      passed: true
    }));

  const completedTasksHistory = (tasks || [])
    .filter(t => t.isCompleted)
    .map(t => ({
      type: t.type,
      title: t.title,
      templateName: t.title,
      completedAt: t.completedAt || new Date(),
      percentage: t.percentage || 0,
      score: t.score || 0,
      total: 100,
      passed: true,
      isAssignment: true
    }));

  // Avoid duplicating items in history if the assignment completion already created a results log
  const filteredCompletedTasks = completedTasksHistory.filter(ct => {
    return !history.some(h => 
      h.type === ct.type && 
      (h.templateName === ct.title || h.title === ct.title || h.scenarioTitle === ct.title || h.templateTitle === ct.title)
    );
  });

  const combinedHistory = [
    ...history,
    ...completedCoursesHistory,
    ...filteredCompletedTasks
  ].sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

  const ITEMS_PER_PAGE = 8;
  const totalHistoryPages = Math.ceil(combinedHistory.length / ITEMS_PER_PAGE);
  const paginatedHistory = combinedHistory.slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE);

  const isFunPlanet = student?.studentCity && (
    student.studentCity.toLowerCase().replace(/[\s-]/g, '') === 'фанпланет' ||
    student.studentCity.toLowerCase().replace(/[\s-]/g, '') === 'funplanet'
  );
  const uncompletedTasks = (tasks || []).filter(t => !t.isCompleted);
  const uncompletedTasksCount = uncompletedTasks.length;

  const loadImage = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
    });
  };

  const handleDownloadPDF = (certificate) => {
    if (!certificate) return;

    const template = certificate.templateId || {};
    const bgType = template.backgroundType || 'classic';
    const verifyUrl = `${window.location.origin}/verify-certificate/${certificate.certificateId}`;

    // Generate QR code locally (no external API, no CORS issues)
    const generateQRDataUrl = async (url) => {
      if (template.hasQrCode === false) return null;
      try {
        const dataUrl = await QRCode.toDataURL(url, { width: 150, margin: 1 });
        return dataUrl;
      } catch (err) {
        console.error('QR generation error:', err);
        return null;
      }
    };

    const logoUrl = `${window.location.origin}${isFunPlanet ? '/FUN-planet-1.png' : '/km-logo.png'}`;
    const studentFullName = `${certificate.studentName} ${certificate.studentLastName}`.trim();
    const dateStr = new Date(certificate.completedAt).toLocaleDateString('uk-UA');

    const loadFont = async (url) => {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result.split(',')[1];
          resolve(base64data);
        };
        reader.readAsDataURL(blob);
      });
    };

    const fontUrlRegular = '/fonts/Roboto-Regular.ttf';
    const fontUrlMedium = '/fonts/Roboto-Medium.ttf';
    const fontUrlItalic = '/fonts/Roboto-Italic.ttf';

    Promise.all([
      loadImage(logoUrl),
      generateQRDataUrl(verifyUrl),
      loadFont(fontUrlRegular),
      loadFont(fontUrlMedium),
      loadFont(fontUrlItalic)
    ]).then(([logoBase64, qrBase64, regularFontBase64, mediumFontBase64, italicFontBase64]) => {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      doc.addFileToVFS('Roboto-Regular.ttf', regularFontBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');

      doc.addFileToVFS('Roboto-Medium.ttf', mediumFontBase64);
      doc.addFont('Roboto-Medium.ttf', 'Roboto', 'bold');

      doc.addFileToVFS('Roboto-Italic.ttf', italicFontBase64);
      doc.addFont('Roboto-Italic.ttf', 'Roboto', 'italic');

      doc.setFont('Roboto', 'normal');

      if (bgType === 'classic') {
        // Classic styling
        doc.setFillColor(253, 251, 247); // Light cream background
        doc.rect(0, 0, 297, 210, 'F');
        
        // Borders
        doc.setDrawColor(197, 160, 89); // Gold border (#c5a059)
        doc.setLineWidth(1.5);
        doc.rect(8, 8, 281, 194);
        doc.setLineWidth(0.6);
        doc.rect(10.5, 10.5, 276, 189);

        // Corner Ornaments
        const corners = [
          { x: 10.5, y: 10.5, dx: 1, dy: 1 },
          { x: 286.5, y: 10.5, dx: -1, dy: 1 },
          { x: 10.5, y: 199.5, dx: 1, dy: -1 },
          { x: 286.5, y: 199.5, dx: -1, dy: -1 }
        ];
        corners.forEach(c => {
          doc.rect(c.x - (c.dx > 0 ? 0 : 5), c.y - (c.dy > 0 ? 0 : 5), 5, 5);
          doc.line(c.x, c.y, c.x + c.dx * 8, c.y + c.dy * 8);
        });

        // Corporate Logo
        if (logoBase64) {
          doc.addImage(logoBase64, 'PNG', 132.5, 15, 32, 16);
        }
        
        // Texts
        doc.setTextColor(138, 109, 37); // Gold title text (#8a6d25)
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(28);
        doc.text(template.titleText || 'СЕРТИФІКАТ', 148.5, 46, { align: 'center' });

        doc.setTextColor(100, 116, 139); // Gray subtext
        doc.setFont('Roboto', 'italic');
        doc.setFontSize(13);
        doc.text(template.subText || 'підтверджує, що', 148.5, 56, { align: 'center' });

        doc.setTextColor(15, 23, 42); // Navy name text
        doc.setFont('Roboto', 'italic');
        doc.setFontSize(30);
        doc.text(studentFullName, 148.5, 76, { align: 'center' });

        // Elegant Divider (Lines + Diamond)
        doc.setDrawColor(197, 160, 89);
        doc.setLineWidth(0.5);
        doc.line(90, 85, 135, 85);
        doc.line(162, 85, 207, 85);
        doc.setFillColor(197, 160, 89);
        doc.triangle(148.5, 83.5, 147, 85, 150, 85, 'F');
        doc.triangle(148.5, 86.5, 147, 85, 150, 85, 'F');

        doc.setTextColor(100, 116, 139);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(12);
        doc.text('успішно завершив(ла) курс / тест:', 148.5, 96, { align: 'center' });

        doc.setTextColor(15, 23, 42);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(18);
        doc.text(certificate.testTitle, 148.5, 108, { align: 'center' });

        doc.setTextColor(100, 116, 139);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(12);
        doc.text(`із результатом: ${certificate.score}%`, 148.5, 118, { align: 'center' });

        // Left footer: QR and Date
        if (qrBase64) {
          doc.addImage(qrBase64, 'PNG', 35, 138, 26, 26);
          doc.setTextColor(100, 116, 139);
          doc.setFont('Roboto', 'normal');
          doc.setFontSize(8);
          doc.text(`Код: ${certificate.certificateId}`, 48, 168, { align: 'center' });
        }
        doc.setTextColor(100, 116, 139);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(8);
        doc.text('ДАТА ВИДАЧІ', 48, 174, { align: 'center' });
        doc.setTextColor(15, 23, 42);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(10);
        doc.text(dateStr, 48, 180, { align: 'center' });

        // Center footer: Gold Seal
        const sealX = 148.5;
        const sealY = 156;
        // Ribbons
        doc.setFillColor(197, 160, 89);
        doc.triangle(142, 162, 134, 185, 140, 182, 'F');
        doc.triangle(155, 162, 163, 185, 157, 182, 'F');
        // Ridges
        doc.setDrawColor(212, 175, 55);
        doc.setLineWidth(0.5);
        for (let a = 0; a < 360; a += 10) {
          const rad = a * Math.PI / 180;
          doc.line(
            sealX + Math.cos(rad) * 12,
            sealY + Math.sin(rad) * 12,
            sealX + Math.cos(rad) * 15,
            sealY + Math.sin(rad) * 15
          );
        }
        // Circle outer
        doc.setFillColor(244, 222, 156);
        doc.circle(sealX, sealY, 12.2, 'FD');
        doc.circle(sealX, sealY, 10.5, 'D');
        // Seal texts
        doc.setTextColor(92, 59, 0);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(6.5);
        doc.text('ServIQ', sealX, 153.5, { align: 'center' });
        doc.setFontSize(4.8);
        doc.text('ЗНАК ЯКОСТІ', sealX, 157.5, { align: 'center' });
        doc.setFontSize(4.2);
        doc.text('ВЕРИФІКОВАНО', sealX, 160.5, { align: 'center' });

        // Right footer: Signature
        doc.setTextColor(30, 58, 138); // Cursive script color
        doc.setFont('Roboto', 'italic');
        doc.setFontSize(18);
        doc.text(template.signatureName || 'Адміністрація', 248, 156, { align: 'center' });

        doc.setDrawColor(100, 116, 139);
        doc.setLineWidth(0.4);
        doc.line(213, 162, 283, 162);

        doc.setTextColor(100, 116, 139);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(8);
        doc.text((template.signatureText || 'Тренер').toUpperCase(), 248, 168, { align: 'center' });
        doc.setTextColor(15, 23, 42);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(10);
        doc.text(template.signatureName || 'Адміністрація', 248, 174, { align: 'center' });

      } else if (bgType === 'modern') {
        // Modern styling
        doc.setFillColor(15, 23, 42); // Dark slate background
        doc.rect(0, 0, 297, 210, 'F');

        // Left decorative bar
        doc.setFillColor(79, 70, 229); // Indigo accents
        doc.rect(0, 0, 15, 210, 'F');
        doc.setFillColor(16, 185, 129); // Emerald accent
        doc.rect(0, 180, 15, 30, 'F');

        // Ornaments
        doc.setDrawColor(79, 70, 229);
        doc.setLineWidth(1);
        doc.rect(25, 15, 257, 180);

        // Corporate Logo
        if (logoBase64) {
          doc.addImage(logoBase64, 'PNG', 137, 22, 32, 16);
        }

        // Texts
        doc.setTextColor(255, 255, 255);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(32);
        doc.text(template.titleText || 'СЕРТИФІКАТ', 153, 52, { align: 'center' });

        doc.setTextColor(156, 163, 175);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(14);
        doc.text(template.subText || 'підтверджує, що', 153, 67, { align: 'center' });

        doc.setTextColor(16, 185, 129); // Emerald
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(26);
        doc.text(studentFullName, 153, 90, { align: 'center' });

        doc.setTextColor(156, 163, 175);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(14);
        doc.text('успішно пройшов(ла) оцінювання:', 153, 107, { align: 'center' });

        doc.setTextColor(255, 255, 255);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(20);
        doc.text(certificate.testTitle, 153, 122, { align: 'center' });

        doc.setTextColor(16, 185, 129);
        doc.setFontSize(13);
        doc.text(`Результат: ${certificate.score}%`, 153, 134, { align: 'center' });

        // Signatures
        doc.setDrawColor(156, 163, 175);
        doc.line(45, 175, 115, 175);
        doc.setTextColor(156, 163, 175);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(11);
        doc.text(template.signatureText || 'Тренер', 80, 181, { align: 'center' });
        doc.setTextColor(255, 255, 255);
        doc.setFont('Roboto', 'bold');
        doc.text(template.signatureName || 'Адміністрація', 80, 187, { align: 'center' });

        // Date
        doc.setTextColor(156, 163, 175);
        doc.setFont('Roboto', 'normal');
        doc.text(`Дата: ${dateStr}`, 45, 160);

        if (qrBase64) {
          doc.addImage(qrBase64, 'PNG', 230, 145, 30, 30);
        }

      } else {
        // Minimalist styling
        doc.setFillColor(255, 255, 255); // White background
        doc.rect(0, 0, 297, 210, 'F');

        // Borders
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.5);
        doc.rect(15, 15, 267, 180);

        // Corporate Logo
        if (logoBase64) {
          doc.addImage(logoBase64, 'PNG', 132.5, 22, 32, 16);
        }

        // Texts
        doc.setTextColor(17, 24, 39); // Charcoal
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(26);
        doc.text(template.titleText || 'СЕРТИФІКАТ', 148, 52, { align: 'center' });

        doc.setTextColor(107, 114, 128);
        doc.setFontSize(12);
        doc.text(template.subText || 'підтверджує, що', 148, 67, { align: 'center' });

        doc.setTextColor(17, 24, 39);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(24);
        doc.text(studentFullName, 148, 90, { align: 'center' });

        doc.setTextColor(107, 114, 128);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(12);
        doc.text('виконав(ла) вимоги та здав(ла) тест:', 148, 107, { align: 'center' });

        doc.setTextColor(17, 24, 39);
        doc.setFont('Roboto', 'bold');
        doc.setFontSize(18);
        doc.text(certificate.testTitle, 148, 122, { align: 'center' });

        doc.setFont('Roboto', 'normal');
        doc.setFontSize(12);
        doc.text(`Відсоток успішності: ${certificate.score}%`, 148, 134, { align: 'center' });

        // Signatures
        doc.setDrawColor(209, 213, 219);
        doc.line(40, 175, 110, 175);
        doc.setTextColor(107, 114, 128);
        doc.setFontSize(11);
        doc.text(template.signatureText || 'Тренер', 75, 181, { align: 'center' });
        doc.setTextColor(17, 24, 39);
        doc.setFont('Roboto', 'bold');
        doc.text(template.signatureName || 'Адміністрація', 75, 187, { align: 'center' });

        // Date
        doc.setTextColor(107, 114, 128);
        doc.setFont('Roboto', 'normal');
        doc.text(`Дата: ${dateStr}`, 40, 160);

        if (qrBase64) {
          doc.addImage(qrBase64, 'PNG', 215, 145, 30, 30);
        }
      }

      doc.save(`certificate-${certificate.certificateId}.pdf`);
    }).catch((err) => {
      console.error('Failed to generate premium PDF:', err);
    });
  };

  const openTask = (task) => {
    const origin = window.location.origin;
    let url = '';
    switch (task.type) {
      case 'quiz':
        url = `${origin}/quiz/${task.hash}?assigned=1`;
        break;
      case 'desk':
        url = `${origin}/test/${task.hash}?assigned=1`;
        break;
      case 'multi-desk':
        url = `${origin}/multi-test/${task.hash}?assigned=1`;
        break;
      case 'game':
        url = `${origin}/game/${task.hash}?assigned=1`;
        break;
      case 'complex':
        url = `${origin}/complex/${task.hash}?assigned=1`;
        break;
      case 'pizza':
        url = `${origin}/pizza/play/${task.hash}?assigned=1`;
        break;
      default:
        break;
    }
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getGroupedCourses = () => {
    if (!data.courses || data.courses.length === 0) return [];
    
    const activeCourses = data.courses.filter(item => !item.progress?.isCompleted);
    
    const groups = {};
    const stagesMap = {};
    
    activeCourses.forEach(item => {
      const stage = item.course?.stageId;
      if (stage && stage._id) {
        const stageIdStr = stage._id.toString();
        if (!groups[stageIdStr]) {
          groups[stageIdStr] = [];
          stagesMap[stageIdStr] = stage;
        }
        groups[stageIdStr].push(item);
      } else {
        if (!groups['general']) {
          groups['general'] = [];
        }
        groups['general'].push(item);
      }
    });
    
    // Sort stages by order, then name
    const sortedStages = Object.values(stagesMap).sort((a, b) => {
      const orderA = typeof a.order === 'number' ? a.order : 0;
      const orderB = typeof b.order === 'number' ? b.order : 0;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name, 'uk');
    });
    
    const result = sortedStages.map(stage => ({
      id: stage._id.toString(),
      name: stage.name,
      description: stage.description,
      courses: groups[stage._id.toString()]
    }));
    
    if (groups['general'] && groups['general'].length > 0) {
      result.push({
        id: 'general',
        name: 'Загальні курси',
        description: 'Курси без прив\'язки до конкретного етапу навчання',
        courses: groups['general']
      });
    }
    
    return result;
  };

  return (
    <div className="sd-container">
      {/* Header */}
      <header className="sd-header">
        <div className="sd-header-left">
          <img 
            src={isFunPlanet ? "/FUN-planet-1.png" : "/km-logo.png"} 
            alt={isFunPlanet ? "Фан Планет" : "Країна Мрій"} 
            className="sd-logo-img" 
          />
          <div className="sd-welcome">
            <h1>Привіт, {student.studentName}! 👋</h1>
            <p>Твій персональний кабінет навчання ServIQ</p>
          </div>
        </div>
        <div className="sd-header-right" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button 
            className="sd-btn-theme-toggle" 
            onClick={toggleTheme} 
            title={theme === 'dark' ? 'Світла тема' : 'Темна тема'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--card-bg)',
              border: '1px solid var(--border)',
              color: 'var(--text-main)',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'var(--transition)'
            }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button className="sd-btn-logout" onClick={onLogout}>
            <LogOut size={18} />
            <span>Вийти</span>
          </button>
        </div>
      </header>
      
      {/* Uncompleted Tasks Alert Banner */}
      {uncompletedTasksCount > 0 && (
        <div className="sd-alert-banner">
          <div className="sd-alert-content">
            <ClipboardList className="sd-alert-icon" size={24} />
            <div className="sd-alert-text">
              <strong>У вас є невиконані завдання!</strong>
              <span>Призначено {uncompletedTasksCount} {uncompletedTasksCount === 1 ? 'нове завдання' : uncompletedTasksCount < 5 ? 'нові завдання' : 'нових завдань'}, які потрібно пройти.</span>
            </div>
          </div>
          <button className="sd-alert-btn" onClick={() => handleTabChange('tasks')}>
            <span>Перейти до завдань</span>
            <Play size={14} fill="currentColor" />
          </button>
        </div>
      )}

      {/* Overview Cards */}
      <section className="sd-stats-grid">
        <div className="sd-stat-card">
          <div className="sd-stat-icon red-glow"><Trophy size={28} /></div>
          <div className="sd-stat-info">
            <h3>{student.avgScore}%</h3>
            <p>Середній бал</p>
          </div>
        </div>
        <div className="sd-stat-card">
          <div className="sd-stat-icon green-glow"><CheckCircle size={28} /></div>
          <div className="sd-stat-info">
            <h3>{student.totalTests}</h3>
            <p>Пройдених тестів</p>
          </div>
        </div>
        <div className="sd-stat-card">
          <div className="sd-stat-icon blue-glow"><MapPin size={28} /></div>
          <div className="sd-stat-info">
            <h3>{student.studentCity || 'Не вказано'}</h3>
            <p>Локація / Місто</p>
          </div>
        </div>
        <div className="sd-stat-card">
          <div className="sd-stat-icon yellow-glow"><TrendingUp size={28} /></div>
          <div className="sd-stat-info">
            <h3>{student.xp || 0} XP</h3>
            <p>Набраний досвід</p>
          </div>
        </div>
        <div className="sd-stat-card">
          <div className="sd-stat-icon orange-glow"><Flame size={28} /></div>
          <div className="sd-stat-info">
            <h3>{student.streakDays || 0} дн.</h3>
            <p>Серія активності</p>
          </div>
        </div>
        <div className="sd-stat-card">
          <div className="sd-stat-icon yellow-glow" style={{ color: '#fbbf24', textShadow: '0 0 10px rgba(251, 191, 36, 0.3)' }}><Coins size={28} /></div>
          <div className="sd-stat-info">
            <h3>{student.coins || 0}</h3>
            <p>ServIQ Coins</p>
          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="sd-main-layout">
        <div className="sd-content-area">
          {/* Tabs */}
          <div className="sd-tabs">
            <button 
              className={`sd-tab-btn ${activeTab === 'courses' ? 'active' : ''}`}
              onClick={() => handleTabChange('courses')}
            >
              <BookOpen size={18} />
              <span>Курси навчання ({data.courses?.filter(c => !c.progress?.isCompleted).length || 0})</span>
            </button>
            <button 
              className={`sd-tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
              onClick={() => handleTabChange('tasks')}
            >
              <ClipboardList size={18} />
              <span>Завдання</span>
              {uncompletedTasksCount > 0 && (
                <span className="sd-tasks-badge">{uncompletedTasksCount}</span>
              )}
            </button>
            <button 
              className={`sd-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => handleTabChange('history')}
            >
              <Clock size={18} />
              <span>Історія ({combinedHistory.length})</span>
            </button>
            <button 
              className={`sd-tab-btn ${activeTab === 'certificates' ? 'active' : ''}`}
              onClick={() => handleTabChange('certificates')}
            >
              <Award size={18} />
              <span>Сертифікати ({certificates.length})</span>
            </button>
            <button 
              className={`sd-tab-btn ${activeTab === 'recommendations' ? 'active' : ''}`}
              onClick={() => handleTabChange('recommendations')}
            >
              <Award size={18} />
              <span>Поради</span>
            </button>
            <button 
              className={`sd-tab-btn ${activeTab === 'leaderboard' ? 'active' : ''}`}
              onClick={() => handleTabChange('leaderboard')}
            >
              <Trophy size={18} />
              <span>Рейтинг</span>
            </button>
            <button 
              className={`sd-tab-btn ${activeTab === 'store' ? 'active' : ''}`}
              onClick={() => handleTabChange('store')}
            >
              <ShoppingBag size={18} />
              <span>Магазин</span>
            </button>
            <button 
              className={`sd-tab-btn ${activeTab === 'guide' ? 'active' : ''}`}
              onClick={() => handleTabChange('guide')}
            >
              <HelpCircle size={18} />
              <span>Як користуватися</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="sd-tab-content">
            {activeTab === 'courses' && (
              <div className="sd-courses-list-tab">
                {(!data.courses || data.courses.length === 0) ? (
                  <div className="sd-empty-state">
                    <p>Для вас наразі немає призначених курсів навчання.</p>
                  </div>
                ) : (
                  <div className="sd-grouped-stages-container">
                    {getGroupedCourses().map(group => (
                      <div key={group.id} className="sd-stage-group-section">
                        <div className="sd-stage-group-header">
                          <h3>📂 {group.name}</h3>
                          {group.description && <p className="sd-stage-group-desc">{group.description}</p>}
                        </div>
                        <div className="sd-courses-grid-tab">
                          {group.courses.map((item) => {
                            const { course, progress } = item;
                            const isCompleted = progress.isCompleted;
                            return (
                              <div key={course._id} className={`sd-course-card-tab ${isCompleted ? 'completed' : ''}`}>
                                {course.coverImage ? (
                                  <div className="sd-course-cover-tab" style={{ backgroundImage: `url(${course.coverImage})` }} />
                                ) : (
                                  <div className="sd-course-cover-tab default-grad" />
                                )}
                                <div className="sd-course-info-tab">
                                  <h3>{course.title}</h3>
                                  <p className="sd-course-desc-tab">{course.description || 'Опис відсутній'}</p>
                                  
                                  <div className="sd-course-progress-tab">
                                    <div className="sd-course-progress-text-tab">
                                      <span>Прогрес навчання:</span>
                                      <strong>{progress.percentage}%</strong>
                                    </div>
                                    <div className="sd-course-progress-bar-tab">
                                      <div className="sd-course-progress-bar-fill-tab" style={{ width: `${progress.percentage}%` }} />
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="sd-course-actions-tab">
                                  <button className="sd-btn-play-tab" onClick={() => navigate(`/course-play/${course._id}`)}>
                                    <Play size={16} fill="currentColor" />
                                    <span>{progress.percentage > 0 ? 'Продовжити' : 'Почати навчання'}</span>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'tasks' && (
              <div className="sd-tasks-list">
                {uncompletedTasks.length === 0 ? (
                  <div className="sd-empty-state">
                    <p>У вас немає активних завдань.</p>
                  </div>
                ) : (
                  uncompletedTasks.map((task) => (
                    <div key={task.id} className="sd-task-item">
                      <div className="sd-task-info">
                        <span className="sd-task-type">{TYPE_LABELS[task.type] || task.type}</span>
                        <h3>{task.title}</h3>
                        {task.description && <p>{task.description}</p>}
                      </div>
                      <div className="sd-task-action">
                        <button className="sd-btn-play" onClick={() => openTask(task)}>
                          <Play size={16} fill="currentColor" />
                          <span>Почати</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="sd-history-list">
                {combinedHistory.length === 0 ? (
                  <div className="sd-empty-state">
                    <p>Ви ще не проходили жодного тесту чи курсу.</p>
                  </div>
                ) : (
                  <>
                    {paginatedHistory.map((item, idx) => (
                      <div key={idx} className="sd-history-item">
                        <div className="sd-history-meta">
                          <span className="sd-task-type">{TYPE_LABELS[item.type] || item.type}</span>
                          <h4>{item.templateName || item.title || item.scenarioTitle || item.templateTitle || 'Тест'}</h4>
                          <span className="sd-history-date">
                            {new Date(item.completedAt).toLocaleDateString('uk-UA')} {new Date(item.completedAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="sd-history-score-area">
                          <div className={`sd-history-score ${item.passed ? 'pass-text' : 'fail-text'}`}>
                            {item.type === 'course'
                              ? `Успішно (${item.score}/${item.total} уроків)`
                              : `${item.percentage}% (${item.score}/${item.total})`
                            }
                          </div>
                          <span className={`sd-pill ${item.passed ? 'pass-pill' : 'fail-pill'}`}>
                            {item.passed ? 'Пройдено' : 'Не здано'}
                          </span>
                        </div>
                      </div>
                    ))}

                    {totalHistoryPages > 1 && (
                      <div className="sd-pagination">
                        <button 
                          className="sd-pagination-btn"
                          disabled={historyPage === 1}
                          onClick={() => setHistoryPage(prev => Math.max(prev - 1, 1))}
                        >
                          ‹ Попередня
                        </button>
                        <span className="sd-pagination-info">
                          Сторінка {historyPage} з {totalHistoryPages} (всього {combinedHistory.length})
                        </span>
                        <button 
                          className="sd-pagination-btn"
                          disabled={historyPage === totalHistoryPages}
                          onClick={() => setHistoryPage(prev => Math.min(prev + 1, totalHistoryPages))}
                        >
                          Наступна ›
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === 'recommendations' && (
              <div className="sd-rec-container">
                {recommendations ? (
                  <>
                    <div className="sd-rec-summary">
                      <div className="sd-rec-header">
                        <AlertTriangle size={20} className={recommendations.priority === 'high' ? 'warn-high' : 'warn-medium'} />
                        <h3>Аналіз та рекомендації</h3>
                      </div>
                      <p className="sd-rec-appeal">{recommendations.appeal}</p>
                    </div>

                    <div className="sd-rec-actions">
                      <h3>💡 Рекомендовані дії:</h3>
                      <div className="sd-rec-actions-list">
                        {recommendations.actions.map((act, idx) => (
                          <div key={idx} className={`sd-rec-action-item border-${act.priority}`}>
                            <h4>{act.title}</h4>
                            <p>{act.detail}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="sd-empty-state">
                    <p>Рекомендації з'являться після того, як ви пройдете хоча б один тест.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'certificates' && (
              <div className="sd-certificates-list">
                {certificates.length === 0 ? (
                  <div className="sd-empty-state">
                    <p>У вас ще немає згенерованих сертифікатів. Проходьте тести успішно, щоб отримати їх!</p>
                  </div>
                ) : (
                  <div className="sd-certificates-grid">
                    {certificates.map((cert) => (
                      <div key={cert._id} className="sd-cert-card">
                        <div className="sd-cert-icon">
                          <Award size={36} />
                        </div>
                        <div className="sd-cert-info">
                          <h4>{cert.templateId?.titleText || 'СЕРТИФІКАТ'}</h4>
                          <p className="sd-cert-test">{cert.testTitle}</p>
                          <p className="sd-cert-meta">
                            Результат: <strong>{cert.score}%</strong> | Видано: {new Date(cert.completedAt).toLocaleDateString('uk-UA')}
                          </p>
                        </div>
                        <div className="sd-cert-actions">
                          <button className="sd-btn-download-cert" onClick={() => handleDownloadPDF(cert)}>
                            <Download size={16} />
                            <span>Завантажити</span>
                          </button>
                          <a href={`/verify-certificate/${cert.certificateId}`} target="_blank" rel="noopener noreferrer" className="sd-link-verify">
                            Перевірити
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <Leaderboard currentUser={user} />
            )}

            {activeTab === 'guide' && (
              <UserGuide user={user} />
            )}

            {activeTab === 'store' && (
              <Store 
                student={student} 
                onBalanceUpdate={(newBalance) => {
                  setData(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      student: {
                        ...prev.student,
                        coins: newBalance
                      }
                    };
                  });
                }} 
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
